import { useContext, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { ApiClientContext } from '../arcticapi';
import {
  createBrowserPositionProvider,
  lookupCurrentLocation,
  type CurrentLocationResult,
  type PositionProvider,
} from './currentLocation';

export interface UseCurrentLocationOptions {
  positionProvider?: PositionProvider;
}

export interface UseCurrentLocationResult {
  locating: boolean;
  error: Error | null;
  currentLocation: CurrentLocationResult | null;
  clearError: () => void;
  resolveCurrentLocation: () => Promise<CurrentLocationResult>;
}

export function useCurrentLocation(
  options: UseCurrentLocationOptions = {},
): UseCurrentLocationResult {
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [currentLocation, setCurrentLocation] =
    useState<CurrentLocationResult | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const apiClient = useContext(ApiClientContext);

  const dependencies = useMemo(
    () => ({
      positionProvider:
        options.positionProvider ?? createBrowserPositionProvider(),
      apiClient: apiClient ?? undefined,
    }),
    [apiClient, options.positionProvider],
  );

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const resolveCurrentLocation = async (): Promise<CurrentLocationResult> => {
    abortControllerRef.current?.abort();

    const controller = new AbortController();
    abortControllerRef.current = controller;
    setLocating(true);
    setError(null);

    try {
      const result = await lookupCurrentLocation(dependencies, {
        signal: controller.signal,
      });
      setCurrentLocation(result);
      return result;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }

      const nextError =
        error instanceof Error
          ? error
          : new Error('Unable to get your current location.');
      setError(nextError);
      throw nextError;
    } finally {
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
      setLocating(false);
    }
  };

  return {
    locating,
    error,
    currentLocation,
    clearError: () => setError(null),
    resolveCurrentLocation,
  };
}
